import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Icon from "./icons";
import Loader from "./loader";
import { TableColumn, TableRow } from "./ReusableTable";

interface InfiniteScrollTableProps<T extends TableRow> {
  /** Unique DOM id — used as InfiniteScroll scrollableTarget */
  scrollId: string;
  data: T[] | null;
  columns: TableColumn<T>[];
  hasMore: boolean;
  onLoadMore: () => void;
  height?: string;
  enableSorting?: boolean;
  getRowActions?: (item: T) => React.ReactNode;
}

function InfiniteScrollTable<T extends TableRow>({
  scrollId,
  data,
  columns,
  hasMore,
  onLoadMore,
  height = "50vh",
  enableSorting = true,
  getRowActions,
}: InfiniteScrollTableProps<T>) {
  const [sortedData, setSortedData] = useState<T[] | null>(data);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc" | "none";
  }>({ key: null, direction: "none" });

  useEffect(() => {
    setSortedData(data);
    // Reset sort when data resets (new search)
    if (!data || data.length === 0) {
      setSortConfig({ key: null, direction: "none" });
    }
  }, [data]);

  const handleSort = (key: string) => {
    if (!enableSorting || !sortedData) return;

    let direction: "asc" | "desc" | "none";
    if (sortConfig.key !== key) {
      direction = "asc";
    } else if (sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.direction === "desc") {
      direction = "none";
    } else {
      direction = "asc";
    }

    setSortConfig({ key, direction });

    if (direction === "none") {
      setSortedData(data ? [...data] : null);
      return;
    }

    const sorted = [...sortedData].sort((a, b) => {
      const aVal = a[key];
      const bVal = b[key];
      if (aVal == null) return direction === "asc" ? 1 : -1;
      if (bVal == null) return direction === "asc" ? -1 : 1;
      if (typeof aVal === "number" && typeof bVal === "number") {
        return direction === "asc" ? aVal - bVal : bVal - aVal;
      }
      return (
        String(aVal).localeCompare(String(bVal), undefined, { numeric: true }) *
        (direction === "asc" ? 1 : -1)
      );
    });

    setSortedData(sorted);
  };

  const getSortIcon = (key: string) => {
    if (sortConfig.key !== key || sortConfig.direction === "none") {
      return <Icon name="arrowupdown" size={16} className="opacity-40" />;
    }
    return sortConfig.direction === "asc" ? (
      <Icon name="arrowup" size={16} className="opacity-70" />
    ) : (
      <Icon name="arrowdown" size={16} className="opacity-70" />
    );
  };

  const colCount = columns.length + (getRowActions ? 1 : 0);

  return (
    <div
      className="bg-white border border-[#043E6133] rounded-[5px] p-[5px] flex flex-col"
      style={{ height }}
    >
      <div
        id={scrollId}
        className="overflow-x-auto overflow-y-auto flex-1"
      >
      <InfiniteScroll
        dataLength={sortedData?.length || 0}
        next={onLoadMore}
        hasMore={sortedData !== null && hasMore}
        loader={
          <div className="text-center py-4">
            <Loader />
          </div>
        }
        scrollableTarget={scrollId}
        style={{ overflow: "visible" }}
      >
        <table className="w-full">
          <thead className="bg-[#e3e4e9] sticky top-0 z-10">
            <tr>
              {columns.map((col) => (
                <th
                  key={String(col.key)}
                  style={{ width: col.width }}
                  className={`px-3 py-3 text-xs font-medium uppercase tracking-wider text-${
                    col.align || "left"
                  }${col.className ? " " + col.className : ""}`}
                >
                  {col.sortable !== false && enableSorting ? (
                    <div
                      className={`flex items-center cursor-pointer leading-none gap-1 ${
                        col.align === "right"
                          ? "justify-end"
                          : col.align === "center"
                          ? "justify-center"
                          : "justify-start"
                      }`}
                      onClick={() => handleSort(String(col.key))}
                    >
                      {col.label}
                      {getSortIcon(String(col.key))}
                    </div>
                  ) : (
                    <div>{col.label}</div>
                  )}
                </th>
              ))}
              {getRowActions && (
                <th className="px-3 py-3 text-xs font-medium uppercase tracking-wider text-center">
                  Actions
                </th>
              )}
            </tr>
          </thead>

          {/* Loading state */}
          {sortedData == null && (
            <tbody>
              <tr>
                <td colSpan={colCount} className="px-3 py-6 text-sm text-center">
                  <Loader />
                </td>
              </tr>
            </tbody>
          )}

          {/* Empty state */}
          {sortedData?.length === 0 && (
            <tbody>
              <tr>
                <td colSpan={colCount} className="px-3 py-6 text-sm text-center text-gray-400">
                  No data available
                </td>
              </tr>
            </tbody>
          )}

          {/* Data rows */}
          {sortedData && sortedData.length > 0 && (
            <tbody>
              {sortedData.map((item) => (
                <tr
                  key={String(item.id)}
                  className="border-b border-[#F0F0F0] hover:bg-gray-50 transition-colors duration-150"
                >
                  {columns.map((col, colIndex) => (
                    <td key={colIndex} className="px-3 py-3 text-sm">
                      {col.render ? col.render(item) : item[col.key as string]}
                    </td>
                  ))}
                  {getRowActions && (
                    <td className="px-3 py-3 text-sm">
                      <div className="flex justify-center space-x-2">
                        {getRowActions(item)}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </InfiniteScroll>
      </div>
    </div>
  );
}

export default InfiniteScrollTable;
